import React, { useState, useEffect } from 'react';
import { Button, Drawer, Form, Input, message, notification, Select, Space, Modal } from 'antd';

import { createConnection, initiateConnection } from 'api/connections';
import { IEnvironment } from 'models/connection';

interface INewConnectionFormValues {
    name: string;
    description: string;
    region: string;
    subdomain: string;
    environment: string;
    url: string;
    powerQueryAuthType: string;
}

interface INewConnectionDrawerProps {
    visible: boolean;
    onClose: () => void;
}

export const NewConnectionDrawer: React.FC<INewConnectionDrawerProps> = (props) => {
    const [form] = Form.useForm<INewConnectionFormValues>();
    const [saving, setSaving] = useState(false);
    let environments: any[] = [];
    const [environmentOptions, setEnvironmentOptions] = useState(environments);
    const [isMicrosoftLoggedIn, setIsMicrosoftLoggedIn] = useState(false);
    const [errorModalVisible, setErrorModalVisible] = useState(false);
    const [errorModalMessage, setErrorModalMessage] = useState<string | null>(null);
    const [loadingEnvironments, setLoadingEnvironments] = useState(false);
    const [userAccessToken, setUserAccessToken] = useState<string | null>(null);

    const onClose = () => {
        setIsMicrosoftLoggedIn(false);
        setSaving(false);
        setUserAccessToken(null);
        // Clean up localStorage on close
        localStorage.removeItem("codeVerifier");
        props.onClose();
        form.resetFields();
    };

    const onCreate = async () => {
        setSaving(true);

        try {
            const values = await form.validateFields();
            
            // Prepare the connection payload
            const connectionPayload: any = {
                name: values.name?.trim(),
                description: values.description?.trim(),
                subdomain: values.subdomain?.trim(),
                region: values.region?.trim(),
                powerQueryAuthType: values.powerQueryAuthType,
            };

            // If user selected OBO flow, use the captured user assertion token
            if (values.powerQueryAuthType === 'onBehalfOf') {
                console.log('OBO connection selected, checking for user assertion token...');
                if (userAccessToken) {
                    console.log('Using captured user access token for OBO connection');
                    connectionPayload.userAssertionToken = userAccessToken;
                } else {
                    console.warn('No user access token available for OBO connection - connection will fall back to application flow when needed');
                    // Don't fail creation, just log the warning
                }
            }

            const conn = await createConnection(connectionPayload).catch((e: any) => {
                console.warn('error creating connection:', e);
                throw e;
            });

            message.success(`Successfully created the connection: ${conn.name || values.name || 'New Connection'}`);

            onClose();
        } catch (e) {
            setSaving(false);
            console.log('error:', e);
            notification.error({ message: 'Error while creating the connection' });
        }
    };

    useEffect(() => {
        const handleUrlChange = () => {
            const urlParams = new URLSearchParams(window.location.search);
            const code = urlParams.get("code");
            const state = urlParams.get("state");
            const error = urlParams.get("error");
            const errorDescription = urlParams.get("error_description");
            const storedCodeVerifier = localStorage.getItem("codeVerifier");
            
            if (error) {
                // Handle OAuth errors
                console.error('OAuth Error:', error, errorDescription);
                const decodedError = decodeURIComponent(errorDescription || error);
                setErrorModalMessage(`Microsoft OAuth Error: ${decodedError}`);
                setErrorModalVisible(true);
                setUserAccessToken(null);
                // Clean up URL parameters and localStorage
                localStorage.removeItem("codeVerifier");
                window.history.replaceState({}, document.title, window.location.pathname);
                return;
            }
            
            if (code && state && storedCodeVerifier) {
                setIsMicrosoftLoggedIn(true);
                getUserEnvironments(code, storedCodeVerifier);
                // Clean up URL parameters after successful auth
                localStorage.removeItem("codeVerifier");
                window.history.replaceState({}, document.title, window.location.pathname);
            }
        };

        window.addEventListener("hashchange", handleUrlChange, false);
        handleUrlChange();

        return () => {
            window.removeEventListener("hashchange", handleUrlChange, false);
        };
    }, []);


    const getUserEnvironments = async (code: string, verifier: string) => {
        setLoadingEnvironments(true);
        try {
            const initConnResponse = await initiateConnection({
                code: code,
                verifier: verifier
            });

            // Handle the new response format with environments and userAccessToken
            if (initConnResponse && typeof initConnResponse === 'object' && 'environments' in initConnResponse) {
                // New format: { environments: [...], userAccessToken: "..." }
                const response = initConnResponse as any;
                setEnvironmentOptions(response.environments || []);
                setUserAccessToken(response.userAccessToken || null);
                console.log('Captured user access token for OBO flows:', response.userAccessToken ? 'Yes' : 'No');
            } else {
                // Legacy format: just an array of environments
                setEnvironmentOptions(Array.isArray(initConnResponse) ? initConnResponse : []);
                console.log('No user access token available (legacy response format)');
            }
        } catch (e: any) {
            console.warn('error initiating connection:', e);
            let description = 'Failed to retrieve your Microsoft environments. Please try again.';
            if (e && (e.message || e.error_description || e.response?.data?.error_description)) {
                description = e.error_description || e.message || (e.response?.data?.error_description) || description;
            }
            setErrorModalMessage(description);
            setErrorModalVisible(true);
            setIsMicrosoftLoggedIn(false);
            setUserAccessToken(null);
            // Clean up localStorage on error
            localStorage.removeItem("codeVerifier");
        } finally {
            setLoadingEnvironments(false);
        }
    }

    // Utility function to generate a random string
    const generateRandomString = (minLength: number, maxLength: number) => {
        const validChars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
        const length = Math.floor(Math.random() * (maxLength - minLength + 1)) + minLength;
        let array = new Uint8Array(length);
        window.crypto.getRandomValues(array);
        array = array.map((x) => validChars.charCodeAt(x % validChars.length));
        return String.fromCharCode.apply(null, Array.from(array));
    };

    // Utility function to generate a code verifier
    const generateCodeVerifier = () => {
        return generateRandomString(43, 128);
    };

    // Utility function to generate a code challenge from the code verifier
    const generateCodeChallenge = async (verifier: string | undefined) => {
        const encoder = new TextEncoder();
        const data = encoder.encode(verifier);
        const digest = await window.crypto.subtle.digest("SHA-256", data);
        const base64Digest = btoa(String.fromCharCode(...Array.from(new Uint8Array(digest))));
        const urlSafeDigest = base64Digest.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
        return urlSafeDigest;
    };

    const buildAuthorizationUrl = async () => {
        const verifier = generateCodeVerifier()
        const clientId = process.env.REACT_APP_AAD_APP_CLIENT_ID;
        const redirectUri = encodeURIComponent(process.env.REACT_APP_AAD_APP_REDIRECT_URI || "");
        const responseType = "code";
        // Always request CRM discovery scopes for environment discovery and basic operations
        // For OBO connections, we'll capture the user token and do a separate Power Query token request
        const scopes = [
            'https://globaldisco.crm.dynamics.com/user_impersonation',
            'offline_access'
        ];
        console.log('Using CRM discovery scopes - will handle Power Query separately for OBO connections');
        const state = generateRandomString(16, 64);
        const codeChallenge = await generateCodeChallenge(verifier);

        localStorage.setItem("codeVerifier", verifier);

        const scopeString = encodeURIComponent(scopes.join(' '));
        const url = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?client_id=${clientId}&response_type=${responseType}&redirect_uri=${redirectUri}&scope=${scopeString}&state=${state}&code_challenge_method=S256&code_challenge=${codeChallenge}`;

        return url;
    };

    const useMicrosoftLogin = async () => {
        const authorizationUrl = buildAuthorizationUrl();
        window.location.href = await authorizationUrl;
    };

    const updateForm = (selectedEnvironmentUrl: string) => {
        // Find the selected environment object based on its URL
        const selectedEnvironment = environmentOptions.find((e: IEnvironment) => e.Url === selectedEnvironmentUrl);
        if (selectedEnvironment) {
            form.setFieldsValue({
                name: selectedEnvironment.FriendlyName,
                description: selectedEnvironment.Purpose,
                url: selectedEnvironment.Url,
                subdomain: selectedEnvironment.UrlName,
                region: selectedEnvironment.Url.substring(selectedEnvironment.Url.indexOf(selectedEnvironment.UrlName) + selectedEnvironment.UrlName.length + 1, selectedEnvironment.Url.length),
            });
        }
    }

    return (
        <>
            <Drawer
                visible={props.visible || isMicrosoftLoggedIn}
                title="New Connection"
                placement="right"
                onClose={onClose}
                closable={!saving}
                maskClosable={!saving}
                keyboard={false}
                extra={
                    <Space>
                        <Button type="primary" onClick={useMicrosoftLogin}>Use Microsoft ID</Button>
                    </Space>
                }
            >
                <Form form={form} layout="vertical" initialValues={{
                    region: 'crm.dynamics.com',
                    powerQueryAuthType: 'application',
                }}>
                    <Form.Item name="environment" label="Environment" rules={[{ required: true, message: 'Please specify an environment.' }]}>
                        <Select 
                            placeholder={loadingEnvironments ? "Loading environments..." : "Select an environment"} 
                            onChange={updateForm}
                            loading={loadingEnvironments}
                            disabled={loadingEnvironments || environmentOptions.length === 0}
                        >
                            {environmentOptions.map((e: IEnvironment) => (
                                <Select.Option key={e.Url} value={e.Url}>
                                    {e.FriendlyName}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item name="name" label="Name" extra="Provide a friendly name for the connection; one that will help you know what it is. (When you select an environment from the list above, this is automatically set for you. Feel free to change it to whatever you'd like!)" rules={[{ required: true, message: 'The name for the connection is required.' }]}>
                        <Input disabled={saving} />
                    </Form.Item>

                    <Form.Item name="description" label="Description" extra="This field is for you to describe to your future self what this connection is for exactly.">
                        <Input disabled={saving} />
                    </Form.Item>

                    <Form.Item name="url" label="URL">
                        <Input disabled />
                    </Form.Item>
                    
                    <Form.Item name="subdomain" label="Subdomain">
                        <Input disabled />
                    </Form.Item>

                    <Form.Item name="region" label="Domain">
                        <Input disabled />
                    </Form.Item>

                    <Form.Item name="powerQueryAuthType" label="Power Query Authentication" extra="Choose how Power Query operations will be authenticated. Application Identity uses PowerTools app credentials. User Account uses your personal Microsoft account - required for dataflow ownership operations and advanced Power Query features.">
                        <Select disabled={saving}>
                            <Select.Option value="application">Application Identity (Standard)</Select.Option>
                            <Select.Option value="onBehalfOf">My User Account (Required for Dataflow Management)</Select.Option>
                        </Select>
                    </Form.Item>
                </Form>
                <Button type="primary" onClick={onCreate} loading={saving} disabled={saving}>Create</Button>
            </Drawer>
            <Modal
                visible={errorModalVisible}
                title="Microsoft Error"
                onCancel={() => setErrorModalVisible(false)}
                footer={[
                    <Button key="close" type="primary" onClick={() => setErrorModalVisible(false)}>
                        Close
                    </Button>
                ]}
            >
                <p>{errorModalMessage}</p>
            </Modal>
        </>
    );
}
